// Written by Claude Code on 2026-05-31
// User prompt: per-client module/product gating system

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { integrationsAPI } from '../services/api';
import { computeEffectiveModules, DEFAULT_AVAILABLE, DEFAULT_ENABLED, ALWAYS_ON_IDS } from '../config/modules';

const ModulesContext = createContext(null);

export function ModulesProvider({ clientId, children }) {
  const [settings, setSettings]       = useState(null);
  const [effectiveIds, setEffectiveIds] = useState(null);

  const loadSettings = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await integrationsAPI.getSettings();
      const s   = res.data ?? {};
      setSettings(s);
      setEffectiveIds(computeEffectiveModules({
        operatingStates:  s.operating_states   ?? [],
        availableModules: s.available_modules  ?? DEFAULT_AVAILABLE,
        enabledModules:   s.enabled_modules    ?? DEFAULT_ENABLED,
      }));
    } catch {
      setEffectiveIds([...ALWAYS_ON_IDS]);
    }
  }, [clientId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  function isEnabled(moduleId) {
    if (!effectiveIds) return true;  // optimistic while loading
    return effectiveIds.includes(moduleId);
  }

  function isAvailable(moduleId) {
    if (!settings) return true;
    const available = settings.available_modules ?? DEFAULT_AVAILABLE;
    return available.includes(moduleId);
  }

  return (
    <ModulesContext.Provider value={{ isEnabled, isAvailable, effectiveIds, settings, reload: loadSettings }}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  return useContext(ModulesContext) ?? {
    isEnabled:    () => true,
    isAvailable:  () => true,
    effectiveIds: null,
    settings:     null,
    reload:       () => {},
  };
}
